import React from "react";

import { css, cx } from "@emotion/css";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { ChatVisibilityType } from "@/models/MessagingModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { ToggleButton } from "@/uiKit/atoms/ToggleButton.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { MessageData } from "@/uiKit/molecules/chat/MessageDataSchema.ts";

export type MessageBoxVisibilitySelectorProps = {
  className?: string;
};

export const MessageBoxVisibilitySelector: React.FC<MessageBoxVisibilitySelectorProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const patientId = usePatientIdFromURL();
  const isBillable = Queries.condition.useCondition(patientId).isBillable;

  const formContext = useFormContext<MessageData>();
  const visibility = useWatch({
    name: "visibility",
    control: formContext.control,
  });

  return (
    <div className={cx(styles.container, className)}>
      <Tag className={styles.label} label={t("messaging.recipient")} />

      <ToggleButtonGroup>
        {isBillable && (
          <ToggleButton
            data-action="messaging.visibility.select-practitioners"
            toggled={visibility === "practitioners"}
            value="practitioners"
            onToggle={() =>
              formContext.setValue("visibility", "practitioners", {
                shouldValidate: true,
              })
            }
            disabled={!isBillable}
            classes={{
              root: styles.chip,
              toggled: styles.staffBackground,
            }}
          >
            <Typography variant="caption" className={styles.label}>
              {t(visibilityOptions["practitioners"].label)}
            </Typography>
          </ToggleButton>
        )}
        <ToggleButton
          data-action="messaging.visibility.select-all"
          toggled={visibility === "all"}
          disabled={!isBillable}
          onToggle={() =>
            formContext.setValue("visibility", "all", {
              shouldValidate: true,
            })
          }
          value="all"
          classes={{ root: styles.chip, toggled: styles.allBackground }}
        >
          <Typography variant="caption" className={styles.label}>
            {t(visibilityOptions["all"].label)}
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
};

const visibilityOptions = {
  all: {
    value: "all",
    label: "messaging.everybody",
  },
  practitioners: {
    value: "practitioners",
    label: "messaging.medicalStaff",
  },
} satisfies Record<ChatVisibilityType, { value: ChatVisibilityType; label: string }>;

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(4),
  }),
  staffBackground: css({
    background: theme.palette.green.contrastText + " !important",
  }),
  allBackground: css({
    background: theme.palette.background.default + " !important",
  }),
  chip: css`
    border-radius: ${theme.spacing(8)};
  `,
  label: css({
    margin: 0,
  }),
  chevron: css`
    display: flex;
    align-items: center;
  `,
});
